/**
 * Transactions fetched per account, cached for the page's lifetime.
 *
 * Detail panels unmount when a row collapses, so without this every re-expand
 * refetches. Module scope (not component state) so it survives the remounts
 * caused by re-sorting or regrouping the table, and so a sibling component can
 * invalidate an entry after saving an annotation.
 *
 * Shared deliberately: both the panels and the dashboard touch it.
 *
 * In-flight promises are also shared. React Strict Mode (and any double-mount)
 * runs the load effect twice before the first response lands; without this
 * map each mount would fire its own HTTP call for the same account/window.
 */
const txCache = new Map();
/** @type {Map<string, { promise: Promise<any>, cancelled: boolean }>} */
const txInflight = new Map();

export const TX_CACHE_TTL_MS = 5 * 60 * 1000;

export const txCacheKey = (accountNumber, cacheScope) => `${accountNumber}|${cacheScope}`;

/** The cached payload for one account while still fresh, else null. */
export const readTxCache = (accountNumber, cacheScope) => {
  const hit = txCache.get(txCacheKey(accountNumber, cacheScope));
  if (!hit || Date.now() - hit.at >= TX_CACHE_TTL_MS) return null;
  return hit.payload;
};

export const writeTxCache = (accountNumber, cacheScope, payload) => {
  txCache.set(txCacheKey(accountNumber, cacheScope), { payload, at: Date.now() });
};

/**
 * Return the existing in-flight fetch for this key, or start `loader` once and
 * share its promise with every concurrent caller until it settles.
 */
export const getOrLoadTx = (accountNumber, cacheScope, loader) => {
  const key = txCacheKey(accountNumber, cacheScope);
  const cached = readTxCache(accountNumber, cacheScope);
  if (cached) return Promise.resolve(cached);

  const existing = txInflight.get(key);
  if (existing) return existing.promise;

  // Register before starting the loader so a same-tick re-entry joins us.
  let resolveEntry;
  let rejectEntry;
  const entry = {
    cancelled: false,
    promise: new Promise((resolve, reject) => {
      resolveEntry = resolve;
      rejectEntry = reject;
    }),
  };
  txInflight.set(key, entry);

  let started;
  try {
    started = loader();
  } catch (err) {
    txInflight.delete(key);
    rejectEntry(err);
    return entry.promise;
  }

  Promise.resolve(started)
    .then((payload) => {
      // Skip the write if this load was superseded (invalidate/retry) so a
      // late response cannot clobber a fresher cache entry.
      if (!entry.cancelled) writeTxCache(accountNumber, cacheScope, payload);
      resolveEntry(payload);
    })
    .catch((err) => {
      rejectEntry(err);
    })
    .finally(() => {
      if (txInflight.get(key) === entry) txInflight.delete(key);
    });

  return entry.promise;
};

/** Drop one account's entries, whatever window they were fetched for. */
export const invalidateTxCache = (accountNumber) => {
  const prefix = `${accountNumber}|`;
  for (const key of txCache.keys()) {
    if (key.startsWith(prefix)) txCache.delete(key);
  }
  for (const key of [...txInflight.keys()]) {
    if (!key.startsWith(prefix)) continue;
    const entry = txInflight.get(key);
    if (entry) entry.cancelled = true;
    txInflight.delete(key);
  }
};

export const clearTxCache = () => {
  for (const entry of txInflight.values()) {
    entry.cancelled = true;
  }
  txCache.clear();
  txInflight.clear();
};
